import * as React from "react"
import { connect } from "react-redux"

import { Action, ActionType } from "@tenforce/toolbox-button-push-react"

import { Tabs, Panel } from "@tenforce/toolbox-tabs-react"
import { FieldItems, Input } from "@tenforce/toolbox-field-react"
import bem from "../../../../../bem"
import Backdrop from "../../../../common/Backdrop"

import { GlobalState } from "../../../../../store"
import {
    mapDispatchToProps as navigationMapDispatchToProps,
    DispatchProps as NavigationDispatchProps,
} from "../actions"
import {
    mapDispatchToProps as bookmarksMapDispatchToProps,
    DispatchProps as BookmarksDispatchProps,
} from "../../../../bookmarks/actions"
import {
    mapDispatchToProps as shortcutsMapDispatchToProps,
    DispatchProps as ShortcutsDispatchProps,
} from "../../../../shortcuts/actions"
import BookmarksAndShortcuts from "./bookmarksandshortcuts/BookmarksAndShortcuts"
import Smartlists from "./smartlists/Smartlists"
import Workspaces from "./workspaces/Workspaces"
import { Symbols } from "@tenforce/toolbox-fontmap"

import "./Navigation.scss"
import { Labels } from "../../../../../util/Translations"

const b = bem.with("NewUI__navigation")
const navigationVisible = "NewUI__navigationVisible"

interface StateProps extends Navigation.Navigation {
    defaultNavigationTab: number
}

interface DispatchProps
    extends NavigationDispatchProps,
        BookmarksDispatchProps,
        ShortcutsDispatchProps {}

type Props = StateProps & DispatchProps

interface State {
    filter: string
    opened: boolean
}

export class Navigation extends React.PureComponent<Props, State> {
    toggleButton: React.RefObject<HTMLButtonElement>

    constructor(props: Props) {
        super(props)
        this.state = {
            filter: "",
            opened: false,
        }
        this.toggleButton = React.createRef()
    }

    render(): JSX.Element {
        const { selectedTab, updateSelectedTab } = this.props
        const {
            NavigationFilterThroughNavigation,
            NothingFound,
            LoadMore,
            NavigationStructureFull,
            NavigationCollections,
            BookmarksAndShortcuts: BookmarksAndShortcutsLabel,
        } = Labels
        const { filter, opened } = this.state

        return (
            <>
                <Action
                    onClick={this.show}
                    id="showNavigationMenuButton"
                    ref={this.toggleButton}
                    actionType={ActionType.icon}
                    extraClasses="is-inverted"
                >
                    {Symbols.SubMenu}
                </Action>
                <Backdrop
                    onHide={this.hide}
                    show={opened}
                    className={`NewUI ${b("container")}`}
                >
                    <div>
                        <FieldItems
                            isInverted={true}
                            extraClasses="tw-mx-1 tw-my-2"
                        >
                            <Input
                                placeholder={
                                    NavigationFilterThroughNavigation ||
                                    "filter through navigation"
                                }
                                value={filter}
                                id="navigationFilter"
                                onChange={this.onChange}
                                autoFocus={true}
                                type="search"
                            />
                        </FieldItems>

                        <Tabs
                            isInverted={true}
                            defaultSelectedIndex={selectedTab}
                            onTabClick={updateSelectedTab}
                            navigationProps={{ extraClasses: "tw-mx-2" }}
                            navigationItemProps={{ extraClasses: "tw-px-1" }}
                            panelsProps={{ extraClasses: "tw-mb-2 tw-mt-3" }}
                        >
                            <Panel panelTitle={NavigationStructureFull}>
                                <Workspaces
                                    nothingFoundLabel={NothingFound}
                                    loadMoreLabel={LoadMore}
                                    onNavigate={this.hide}
                                />
                            </Panel>
                            <Panel panelTitle={NavigationCollections}>
                                <Smartlists
                                    nothingFoundLabel={NothingFound}
                                    getLabelByType={this.getLabelByType}
                                    loadMoreLabel={LoadMore}
                                    onNavigate={this.hide}
                                />
                            </Panel>
                            <Panel panelTitle={BookmarksAndShortcutsLabel}>
                                <BookmarksAndShortcuts
                                    nothingFoundLabel={NothingFound}
                                    onNavigate={this.hide}
                                />
                            </Panel>
                        </Tabs>
                    </div>
                </Backdrop>
            </>
        )
    }

    show = (): void => {
        const { initializeNavigation, defaultNavigationTab } = this.props
        const appContainer = document.getElementById(
            "app-container",
        ) as HTMLElement
        appContainer.classList.add(navigationVisible)
        this.setState({ opened: true })
        initializeNavigation(defaultNavigationTab)
    }

    hide = (): void => {
        const appContainer = document.getElementById(
            "app-container",
        ) as HTMLElement
        appContainer.classList.remove(navigationVisible)
        this.setState({ opened: false })
    }

    onChange = (ev: React.ChangeEvent<HTMLInputElement>): void => {
        const {
            filterSmartlists,
            filterWorkspacesFull,
            filterBookmarks,
            filterShortcuts,
        } = this.props

        const filter = ev.target.value
        this.setState({ filter })

        filterWorkspacesFull(filter)
        filterSmartlists(filter)
        filterBookmarks(filter)
        filterShortcuts(filter)
    }

    getLabelByType = (type: string): string => {
        const {
            NavigationPrivateCollections: NavigationPrivateSmartlists,
            NavigationSharedByMeCollections: NavigationSharedByMeSmartlists,
            NavigationSharedWithMeCollections: NavigationSharedWithMeSmartlists,
        } = Labels

        switch (type) {
            case "private":
                return NavigationPrivateSmartlists
            case "sharedByMe":
                return NavigationSharedByMeSmartlists
            case "sharedWithMe":
                return NavigationSharedWithMeSmartlists
            default:
                return ""
        }
    }
}

const mapStateToProps = (state: GlobalState): StateProps => ({
    ...state.navigation,
    defaultNavigationTab: state.userInfo.defaultNavigationTab,
})

const mapDispatchToProps: DispatchProps = {
    ...navigationMapDispatchToProps,
    ...bookmarksMapDispatchToProps,
    ...shortcutsMapDispatchToProps,
}

export default connect(mapStateToProps, mapDispatchToProps)(Navigation)
